import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import ChatArea from './components/ChatArea';
import InputArea from './components/InputArea';
import IdleScreen from './components/IdleScreen';
import LoadingSpinner from './components/LoadingSpinner';
import LoadingUpload from './components/LoadingUpload';
import Notification from './components/Notification';
import ModelSwitcherDropdown from './components/ModelSwitcherDropdown';
import ModeSwitcherDropdown from './components/ModeSwitcherDropdown';
import Login from './components/Login';
import CloudStorage from './components/CloudStorage';
import FileStorage from './components/FileStorage';
import KnowledgePackCreator from './components/KnowledgePackCreator';
import KnowledgeStore from './components/KnowledgeStore';
import Modal from 'react-modal';
import './App.css';
import { auth } from './firebase-config'; // Firebase auth import
//import ProfileUI from './components/ProfileUI';  // <-- Import the ProfileUI Component
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from './firebase-config';  // Import Firebase Storage
import Layout from './components/Layout'; // Import the layout
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Set the root element for the modal (typically the root div of your app)
Modal.setAppElement('#root');

function App() {
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState('');
  const [activeModel, setActiveModel] = useState('CUSTOM');
  const [activeMode, setActiveMode] = useState('RAG');
  const [hasUserPrompted, setHasUserPrompted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [user, setUser] = useState(null); // Keep track of user state

  const navigate = useNavigate();

  // Firebase authentication check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        setUser(authUser);  // Set user when logged in
      } else {
        setUser(null);  // Clear user when logged out
        // Optionally, navigate to login if needed
        // navigate('/login');
      }
    });
    return () => unsubscribe(); // Cleanup listener
  }, [navigate]);

  const handleSendPrompt = (prompt) => {
    if (!hasUserPrompted) {
      setHasUserPrompted(true);
    }
    const userMessage = { role: 'user', content: prompt };

    setMessages([...messages, userMessage]);
    setIsAiLoading(true);

    fetch('/ask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `chat_id=${encodeURIComponent(currentChatId)}&prompt=${encodeURIComponent(prompt)}&mode=${encodeURIComponent(activeMode)}`,
    })
      .then(response => response.json())
      .then(data => {
        const aiMessage = { role: 'ai', content: data.response };
        setMessages(prevMessages => [...prevMessages, aiMessage]);
        setIsAiLoading(false);
      })
      .catch(error => {
        console.error("Error generating response:", error);
        setIsAiLoading(false);
      });
  };

  const switchModel = (model) => {
    setIsLoading(true);
    setHasUserPrompted(false);

    fetch('/switch_model', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `model=${model}`,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setActiveModel(model);
          setMessages([]);
          setNotification(`Switched to ${model} model.`);
        } else {
          setNotification(`Failed to switch model: ${data.message}`);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error switching model:", error);
        setNotification(`Error switching model: ${error.message}`);
        setIsLoading(false);
      });
  };

  const switchMode = (mode) => {
    fetch('/switch_mode', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `mode=${encodeURIComponent(mode)}`,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setActiveMode(mode);
          setNotification(`Switched to ${mode} mode.`);
        } else {
          setNotification(`Failed to switch mode: ${data.message}`);
        }
      })
      .catch(error => {
        console.error("Error switching mode:", error);
        setNotification(`Error switching mode: ${error.message}`);
      });
  };

  // Function to upload a file to user-specific cloud storage
  const uploadUserFile = async (file) => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const storageRef = ref(storage, `users/${currentUser.uid}/${file.name}`);
      try {
        // Upload the file to Firebase Storage
        const snapshot = await uploadBytes(storageRef, file);
        console.log('Uploaded a file!');
        
        // Get the download URL for the uploaded file
        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log('File available at', downloadURL);
      } catch (error) {
        console.error("Error uploading file: ", error);
      }
    } else {
      console.log("No user is signed in.");
    }
  };

  const handleFileUpload = (files) => {
    setIsUploading(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files[]', file);
    });

    formData.append('context', activeModel);

    fetch('/upload', {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        setIsUploading(false);
        if (data.success) {
          setNotification("Files successfully uploaded and ingested.");
        } else {
          setNotification(`Failed to upload files: ${data.message}`);
        }
      })
      .catch(error => {
        setIsUploading(false);
        console.error("Error uploading files:", error);
        setNotification("Error uploading files.");
      });
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <>
      <div className="dropdown-container flex justify-between">
        <ModelSwitcherDropdown activeModel={activeModel} switchModel={switchModel} />
        <ModeSwitcherDropdown activeMode={activeMode} switchMode={switchMode} />
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : isUploading ? (
        <LoadingUpload />
      ) : (
        <>
          {!hasUserPrompted ? (
            <IdleScreen onSendPrompt={handleSendPrompt} />
          ) : (
            <ChatArea messages={messages} isAiLoading={isAiLoading} />
          )}
        </>
      )}

      <InputArea onSend={handleSendPrompt} activeModel={activeModel} onFileUpload={handleFileUpload} />
      {notification && <Notification message={notification} onClose={handleCloseNotification} />}
    </>
  );
}

// Wrapping the App in Router
function AppWrapper() {
  return (
    <Router>
      <DndProvider backend={HTML5Backend}>
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Define all your sub-pages here */}
            <Route path="login" element={<Login />} />
            <Route path="storage" element={<CloudStorage />} />
            <Route path="/cloud-storage" element={<FileStorage />} />
            <Route path="/knowledge-pack" element={<KnowledgePackCreator />} />
            <Route path="/knowledge-store" element={<KnowledgeStore />} />
            <Route path="/" element={<App />} />
          </Route>
        </Routes>
      </DndProvider>
    </Router>
  );
}

export default AppWrapper;

